import React, { useEffect, useState } from 'react';

const API = 'https://opmlee572h.execute-api.us-west-2.amazonaws.com';

const emptyForm = {
  serviceId: '', serviceName: '', originalPrice: '',
  discountType: 'percent', discountValue: '',
  title: '', description: '', expiryDate: '',
};

export default function DealsManager({ providerId }) {
  const [deals, setDeals]       = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState(emptyForm);
  const [editId, setEditId]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');

  const fetchData = async () => {
    if (!providerId) return;
    try {
      const [provRes, dealsRes] = await Promise.all([
        fetch(`${API}/providers?providerId=${providerId}`),
        fetch(`${API}/deals/provider?providerId=${providerId}`),
      ]);
      const provData  = await provRes.json();
      const dealsData = await dealsRes.json();
      setServices(provData.services || []);
      setDeals(Array.isArray(dealsData) ? dealsData : []);
    } catch (err) { console.error('DealsManager fetchData error:', err); }
    setLoading(false);
  };

  useEffect(() => { if (providerId) fetchData(); }, [providerId]);

  const handleServiceSelect = (e) => {
    const svcId = e.target.value;
    if (!svcId) { setForm(f => ({ ...f, serviceId: '', serviceName: '', originalPrice: '' })); return; }
    const svc = services.find(s => String(s.id) === svcId);
    if (svc) setForm(f => ({ ...f, serviceId: String(svc.id), serviceName: svc.name, originalPrice: String(svc.price), title: f.title || `Deal on ${svc.name}` }));
  };

  const getDiscountedPrice = () => {
    const orig = parseFloat(form.originalPrice);
    const val  = parseFloat(form.discountValue);
    if (!orig || !val) return null;
    if (form.discountType === 'percent') return Math.max(0, orig * (1 - val / 100)).toFixed(2);
    if (form.discountType === 'flat')    return Math.max(0, orig - val).toFixed(2);
    return null;
  };

  const handleSubmit = async () => {
    if (!form.serviceId || !form.title || !form.discountValue) { setError('Please select a service and fill in title and discount.'); return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      const method = editId ? 'PUT' : 'POST';
      const body   = editId ? { ...form, dealId: editId, providerId } : { ...form, providerId };
      const res    = await fetch(`${API}/deals`, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data   = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save deal');
      setSuccess(editId ? 'Deal updated!' : 'Deal created!');
      setForm(emptyForm); setShowForm(false); setEditId(null); fetchData();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleEdit = (deal) => {
    setForm({ serviceId: deal.serviceId || '', serviceName: deal.serviceName || '', originalPrice: deal.originalPrice?.toString() || '', discountType: deal.discountType || 'percent', discountValue: deal.discountValue?.toString() || '', title: deal.title || '', description: deal.description || '', expiryDate: deal.expiryDate || '' });
    setEditId(deal.dealId); setShowForm(true); setError(''); setSuccess('');
  };

  const handleDelete = async (dealId) => {
    if (!window.confirm('Remove this deal?')) return;
    try {
      const res = await fetch(`${API}/deals?dealId=${dealId}&providerId=${providerId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to remove deal');
      setSuccess('Deal removed.'); fetchData();
    } catch (err) { setError(err.message); }
  };

  const handleCancel = () => { setShowForm(false); setEditId(null); setForm(emptyForm); setError(''); };

  const discountedPrice = getDiscountedPrice();
  const today = new Date().toISOString().split('T')[0];
  const card = { background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '16px', boxShadow: 'var(--shadow-sm)' };
  const inputSt = { width: '100%', padding: '0.75rem 1rem', borderRadius: '10px', border: '1.5px solid var(--border)', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', background: 'var(--cream)', fontFamily: 'DM Sans, sans-serif', color: 'var(--text)', transition: 'border-color 0.2s' };

  if (loading) return <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>Loading deals...</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.5rem', fontWeight: 800, color: 'var(--text)', marginBottom: '0.25rem' }}>Deals & Promotions</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Create special offers tied to your services to attract new clients</p>
        </div>
        {!showForm && services.length > 0 && (
          <button onClick={() => { setShowForm(true); setEditId(null); setForm(emptyForm); setError(''); setSuccess(''); }}
            className="btn-primary" style={{ fontSize: '0.875rem' }}>+ New Deal</button>
        )}
      </div>

      {/* No services warning */}
      {services.length === 0 && (
        <div style={{ ...card, padding: '2rem', display: 'flex', alignItems: 'center', gap: '1.25rem', border: '1.5px solid #fde68a', background: '#fffbeb' }}>
          <div style={{ fontSize: '1.75rem', flexShrink: 0 }}>⚠️</div>
          <div>
            <p style={{ fontWeight: 700, fontSize: '0.9rem', color: '#92400e', marginBottom: '0.25rem' }}>No services added yet</p>
            <p style={{ fontSize: '0.82rem', color: '#a16207' }}>You need at least one service before creating deals. Head to Settings → Services first.</p>
          </div>
        </div>
      )}

      {/* Feedback */}
      {error   && <div style={{ background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: '10px', padding: '0.75rem 1rem', color: '#dc2626', fontSize: '0.85rem' }}>{error}</div>}
      {success && <div style={{ background: 'var(--brand-pale)', border: '1px solid rgba(13,107,110,0.2)', borderRadius: '10px', padding: '0.75rem 1rem', color: 'var(--brand-1)', fontSize: '0.85rem', fontWeight: 600 }}>✓ {success}</div>}

      {/* Create / Edit Form */}
      {showForm && services.length > 0 && (
        <div style={{ ...card, overflow: 'hidden' }}>
          {/* Form header */}
          <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)', background: 'var(--cream)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1rem', color: 'var(--text)' }}>{editId ? 'Edit Deal' : 'Create New Deal'}</p>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>Fill in the details below — clients will see this on your profile</p>
            </div>
            <button onClick={handleCancel} style={{ background: 'var(--cream-mid)', border: '1px solid var(--border)', borderRadius: '8px', padding: '0.4rem 0.875rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', color: 'var(--text-mid)', fontFamily: 'DM Sans, sans-serif' }}>✕ Cancel</button>
          </div>

          <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {/* Step 1 — Service */}
            <div>
              <label style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', display: 'block', marginBottom: '0.5rem' }}>1. Select a Service</label>
              <select style={inputSt} value={form.serviceId} onChange={handleServiceSelect}
                onFocus={e => e.target.style.borderColor = 'var(--brand-1)'} onBlur={e => e.target.style.borderColor = 'var(--border)'}>
                <option value="">— Choose a service —</option>
                {services.map(svc => <option key={svc.id} value={String(svc.id)}>{svc.name} (${svc.price})</option>)}
              </select>
            </div>

            {/* Discount row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem' }}>
              <div>
                <label style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', display: 'block', marginBottom: '0.5rem' }}>2. Discount Type</label>
                <select style={inputSt} value={form.discountType} onChange={e => setForm(f => ({ ...f, discountType: e.target.value, discountValue: '' }))}
                  onFocus={e => e.target.style.borderColor = 'var(--brand-1)'} onBlur={e => e.target.style.borderColor = 'var(--border)'}>
                  <option value="percent">% Percentage off</option>
                  <option value="flat">$ Flat dollar off</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', display: 'block', marginBottom: '0.5rem' }}>
                  {form.discountType === 'percent' ? 'Percentage (e.g. 20)' : 'Amount Off ($)'}
                </label>
                <input type="number" style={inputSt} placeholder={form.discountType === 'percent' ? '20' : '10'} value={form.discountValue}
                  onChange={e => setForm(f => ({ ...f, discountValue: e.target.value }))}
                  onFocus={e => e.target.style.borderColor = 'var(--brand-1)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
              </div>
            </div>

            {/* Price preview */}
            {discountedPrice && form.originalPrice && (
              <div style={{ background: 'var(--brand-pale)', border: '1px solid rgba(13,107,110,0.15)', borderRadius: '10px', padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <span style={{ fontSize: '1.1rem' }}>🏷️</span>
                <div>
                  <p style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--brand-1)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.1rem' }}>Price Preview</p>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text)' }}>
                    <span style={{ textDecoration: 'line-through', color: 'var(--text-muted)', marginRight: '0.5rem' }}>${form.originalPrice}</span>
                    <span style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, color: 'var(--brand-1)', fontSize: '1.1rem' }}>${discountedPrice}</span>
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      ({form.discountType === 'percent' ? `${form.discountValue}% off` : `$${form.discountValue} off`})
                    </span>
                  </p>
                </div>
              </div>
            )}

            {/* Title */}
            <div>
              <label style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', display: 'block', marginBottom: '0.5rem' }}>3. Deal Title</label>
              <input type="text" style={inputSt} placeholder="e.g. Summer Fade Special" value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                onFocus={e => e.target.style.borderColor = 'var(--brand-1)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
            </div>

            {/* Description + expiry */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.875rem' }}>
              <div>
                <label style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', display: 'block', marginBottom: '0.5rem' }}>Description (optional)</label>
                <textarea style={{ ...inputSt, resize: 'vertical', minHeight: '80px' }} placeholder="What makes this deal special?" value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  onFocus={e => e.target.style.borderColor = 'var(--brand-1)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
              </div>
              <div>
                <label style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', display: 'block', marginBottom: '0.5rem' }}>Expiry Date (optional)</label>
                <input type="date" style={inputSt} min={today} value={form.expiryDate}
                  onChange={e => setForm(f => ({ ...f, expiryDate: e.target.value }))}
                  onFocus={e => e.target.style.borderColor = 'var(--brand-1)'} onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.4rem' }}>Leave blank = no expiry</p>
              </div>
            </div>

            {/* Submit */}
            <div style={{ paddingTop: '0.25rem', borderTop: '1px solid var(--border)', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button onClick={handleCancel} style={{ background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: '9px', padding: '0.65rem 1.5rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', color: 'var(--text-mid)' }}>Cancel</button>
              <button onClick={handleSubmit} disabled={saving} className="btn-primary" style={{ opacity: saving ? 0.7 : 1, fontSize: '0.875rem' }}>
                {saving ? 'Saving...' : editId ? 'Save Changes' : 'Create Deal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deals list */}
      {deals.length === 0 && !showForm ? (
        <div style={{ ...card, padding: '4rem 2rem', textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🏷️</div>
          <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.2rem', fontWeight: 800, color: 'var(--text)', marginBottom: '0.5rem' }}>No active deals yet</p>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', maxWidth: '340px', margin: '0 auto 1.75rem', lineHeight: 1.65 }}>Create a deal to offer discounts on your services. Deals show up on your public booking page.</p>
          {services.length > 0 && (
            <button onClick={() => { setShowForm(true); setEditId(null); setForm(emptyForm); }} className="btn-primary" style={{ fontSize: '0.875rem' }}>+ Create your first deal</button>
          )}
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.875rem' }}>
            <p style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>{deals.length} active deal{deals.length !== 1 ? 's' : ''}</p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            {deals.map(deal => {
              const isExpired = deal.expiryDate && deal.expiryDate < today;
              const savings = deal.discountType === 'percent'
                ? `${deal.discountValue}% off`
                : `$${deal.discountValue} off`;
              return (
                <div key={deal.dealId} style={{ ...card, padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', borderLeft: `3px solid ${isExpired ? 'var(--border)' : 'var(--brand-1)'}`, opacity: isExpired ? 0.65 : 1, transition: 'all 0.2s' }}
                  onMouseEnter={e => { if (!isExpired) { e.currentTarget.style.transform = 'translateX(2px)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'translateX(0)'; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1, minWidth: 0 }}>
                    <div style={{ width: 44, height: 44, borderRadius: '12px', background: isExpired ? 'var(--cream)' : 'var(--brand-pale)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem', flexShrink: 0 }}>🏷️</div>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text)', marginBottom: '0.15rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{deal.title}</p>
                      <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                        {deal.serviceName}
                        {deal.originalPrice && <> · <span style={{ textDecoration: 'line-through' }}>${deal.originalPrice}</span></>}
                        {deal.discountType && deal.discountValue && (
                          <span style={{ marginLeft: '0.35rem', color: 'var(--brand-1)', fontWeight: 700 }}>{savings}</span>
                        )}
                      </p>
                      {deal.expiryDate && (
                        <p style={{ fontSize: '0.72rem', color: isExpired ? '#dc2626' : 'var(--text-muted)', marginTop: '0.15rem', fontWeight: isExpired ? 700 : 500 }}>
                          {isExpired ? '⚠️ Expired' : `Expires ${deal.expiryDate}`}
                        </p>
                      )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', flexShrink: 0 }}>
                    {!isExpired && (
                      <div style={{ background: 'var(--brand-pale)', border: '1px solid rgba(13,107,110,0.2)', borderRadius: '20px', padding: '0.25rem 0.75rem', fontSize: '0.72rem', fontWeight: 700, color: 'var(--brand-1)' }}>
                        Active
                      </div>
                    )}
                    <button onClick={() => handleEdit(deal)} style={{ background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: '8px', padding: '0.4rem 0.875rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', color: 'var(--text-mid)', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand-1)'; e.currentTarget.style.color = 'var(--brand-1)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-mid)'; }}>
                      Edit
                    </button>
                    <button onClick={() => handleDelete(deal.dealId)} className="btn-danger" style={{ fontSize: '0.78rem' }}>Remove</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}